import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { MusicVideoLookSettings } from 'fym-shared/src/music24/musicVideoDoc';
import { MUSIC_VIDEO_GRADES, musicVideoGradeFilter } from 'fym-shared/src/music24/musicVideoLook';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { gradeMessages, panelMessages } from '../messages/panelMessages';
import { CutSpeedSlider } from './CutSpeedSlider';
import { ToggleItem } from './ToggleItem';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

type LookSwitch = 'grain' | 'vignette' | 'flashOnDrop' | 'lightLeaks';

const SWITCHES: ReadonlyArray<{
  key: LookSwitch;
  title: keyof typeof panelMessages;
  facts: keyof typeof panelMessages;
}> = [
  { key: 'grain', title: 'grain', facts: 'grainFacts' },
  { key: 'vignette', title: 'vignette', facts: 'vignetteFacts' },
  { key: 'flashOnDrop', title: 'flash', facts: 'flashFacts' },
  { key: 'lightLeaks', title: 'leaks', facts: 'leaksFacts' },
];

/**
 * The Look panel (design d3): one colour grade over all clips (the CSS filters of fym-shared
 * `musicVideoLook.ts`, the same table as the render), the layers on top, and the cut speed of the
 * whole video, which recuts with the AI after a confirm.
 */
const LookPanelComponent: React.FC = () => {
  const intl = useIntl();
  const { doc, data, change, readOnly } = useMusicVideoEditor();
  const { look } = doc;
  const patch = (next: Partial<MusicVideoLookSettings>) =>
    change((current) => ({ ...current, look: { ...current.look, ...next } }));
  const sample = data.song.artworkUrl === null ? undefined : `url("${data.song.artworkUrl}")`;

  return (
    <>
      <h3 className={styles.paneTitle}>{intl.formatMessage(panelMessages.lookTitle)}</h3>
      <span className={styles.eyebrow}>{intl.formatMessage(panelMessages.colour)}</span>
      <div className={styles.gradeGrid} role="radiogroup" aria-label={intl.formatMessage(panelMessages.colour)}>
        {MUSIC_VIDEO_GRADES.map((grade) => (
          <button
            key={grade}
            type="button"
            role="radio"
            aria-checked={grade === look.grade}
            disabled={readOnly}
            className={clsx(styles.gradeTile, grade === look.grade && styles.gradeTileOn)}
            onClick={() => patch({ grade })}
          >
            <span
              className={styles.gradeSample}
              style={{ backgroundImage: sample, filter: musicVideoGradeFilter(grade) }}
            />
            {intl.formatMessage(gradeMessages[grade])}
          </button>
        ))}
      </div>
      <p className={styles.hint}>{intl.formatMessage(panelMessages.gradeHint)}</p>
      <div className={styles.separator} />
      <span className={styles.eyebrow}>{intl.formatMessage(panelMessages.onTop)}</span>
      <div className={styles.column}>
        {SWITCHES.map((item) => (
          <ToggleItem
            key={item.key}
            title={intl.formatMessage(panelMessages[item.title])}
            facts={intl.formatMessage(panelMessages[item.facts])}
            isOn={look[item.key]}
            disabled={readOnly}
            onToggle={() =>
              change((current) => ({ ...current, look: { ...current.look, [item.key]: !current.look[item.key] } }))
            }
          />
        ))}
      </div>
      <div className={styles.separator} />
      <CutSpeedSlider />
    </>
  );
};

export const LookPanel = TypedMemo(LookPanelComponent);
